import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import Button from 'antd/es/button';
import Skeleton from 'antd/es/skeleton';
import Typography from 'antd/es/typography';
import get from 'lodash.get';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { TaskHeader } from '../../components';
import { AgentRunDetails, AgentRunLayout, OutputSection } from './components';
import AgentRunResources from './components/AgentRunResources';

import ContentContainer from '@components/ContentContainer';
import { useTheme } from '@components/ThemeProvider';
import { useAgentRunQuery } from '@queries/agent-runs';
import { useTaskQuery } from '@queries/tasks';

const { Title, Paragraph } = Typography;

// Styled components
const BackButton = styled(Button)`
  margin-bottom: 16px;
`;

// Loading state components
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.token?.marginMD || '16px'};
  padding: ${({ theme }) => theme.token?.paddingLG || '24px'};
`;

const SkeletonHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.token?.marginLG || '24px'};
`;

const SkeletonLayout = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.token?.marginLG || '24px'};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SkeletonMain = styled.div`
  flex: 1;
`;

const SkeletonSidebar = styled.div`
  width: 300px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

/**
 * Agent Run Details Page
 *
 * Displays the details of a specific agent run, including:
 * - Output
 * - Thought process
 * - Run information (status, timestamps, progress)
 */
const AgentRunDetailsPage: React.FC = () => {
  const { taskId, agentRunId, teamId, projectId } = useParams<{
    taskId: string;
    agentRunId: string;
    teamId: string;
    projectId: string;
  }>();

  const { themeToken } = useTheme();
  const primaryColor = themeToken?.colorPrimary || '#499D9C';

  const { data: agentRun, isLoading: isAgentRunLoading } = useAgentRunQuery(
    agentRunId as string,
    !!agentRunId,
  );

  const { data: task, isLoading: isTaskLoading } = useTaskQuery(
    taskId as string,
    !!taskId,
  );

  // Extract relevant data from agent run
  // The agent output is a dictionary and path we need is output.data.output
  const agentOutput = useMemo(() => {
    if (!agentRun?.agentOutput) return null;
    return get(agentRun.agentOutput, 'data.output', null);
  }, [agentRun]);

  // Determine agent status
  const agentStatus = useMemo(() => {
    if (!agentRun) return 'Unknown';

    return agentRun.failedAt
      ? 'Failed'
      : agentRun.currentStep && agentRun.maxActions
        ? agentRun.currentStep >= agentRun.maxActions
          ? 'Completed'
          : 'In Progress'
        : 'Unknown';
  }, [agentRun]);

  // Render loading skeleton
  const renderLoadingSkeleton = () => (
    <ContentContainer showFullWidthToggle={false} forceFullWidth>
      <ContentContainer.Header
        title={
          <SkeletonHeader>
            <Skeleton.Input active style={{ width: '60%', height: 32 }} />
            <div style={{ marginTop: 8 }}>
              <Skeleton.Input active style={{ width: '40%', height: 20 }} />
            </div>
          </SkeletonHeader>
        }
      />
      <LoadingContainer>
        <SkeletonLayout>
          <SkeletonMain>
            <Skeleton active paragraph={{ rows: 6 }} />
          </SkeletonMain>
          <SkeletonSidebar>
            <Skeleton active paragraph={{ rows: 4 }} />
          </SkeletonSidebar>
        </SkeletonLayout>
      </LoadingContainer>
    </ContentContainer>
  );

  // Handle loading state
  if (isAgentRunLoading || !agentRunId || !teamId || isTaskLoading || !taskId) {
    return renderLoadingSkeleton();
  }

  // Handle not found state
  if (!agentRun) {
    return (
      <ContentContainer showFullWidthToggle={false} forceFullWidth>
        <ContentContainer.Header
          title={
            <BackButton
              type="text"
              icon={<ArrowLeftOutlined />}
              href={`/team/${teamId}/project/${projectId}/tasks/${taskId}`}
            >
              Back to task
            </BackButton>
          }
        />
        <div style={{ padding: '24px' }}>
          <div>
            <Title level={4}>Agent run not found</Title>
            <Paragraph>The requested agent run could not be found.</Paragraph>
          </div>
        </div>
      </ContentContainer>
    );
  }

  // No-op functions for required props
  const handleEditName = () => {
    /* no-op */
  };
  const handleStartTask = () => {
    /* no-op */
  };

  return (
    <ContentContainer showFullWidthToggle={false} forceFullWidth>
      <ContentContainer.Header
        title={
          <TaskHeader
            title={task?.title || ''}
            onEditTitle={handleEditName}
            colorPrimary={primaryColor}
            onStartTask={handleStartTask}
          />
        }
      />
      <AgentRunLayout
        mainContent={<OutputSection output={agentOutput} />}
        sidebarContent={
          <div style={{ overflow: 'auto', height: '100%' }}>
            <AgentRunDetails
              agentStatus={agentStatus}
              agentRunId={agentRunId}
            />
            <AgentRunResources agentRunId={agentRunId} />
          </div>
        }
      />
    </ContentContainer>
  );
};

export default AgentRunDetailsPage;
